import { DataTable, LoadingBlock, Money, Pill, Section, StatCard, useAdminResource } from "./AdminUI";

/** Revenue against what the providers actually charged to produce the videos. */
export default function MarginPanel() {
  const { data, loading, error } = useAdminResource("/admin/margin");
  if (loading) return <LoadingBlock label="Loading margin" />;
  if (error) return <div className="brutal-card p-6 text-ink-accent text-sm" role="alert">{error}</div>;

  const kinds = Object.entries(data.cost_by_kind || {}).sort((a, b) => b[1] - a[1]);
  const worst = (data.users || []).filter((u) => u.margin_usd < 0);

  return (
    <>
      <Section id="margin" title="// MARGIN AFTER PROVIDER COSTS">
        <div className="grid md:grid-cols-3 gap-4">
          <StatCard label="MRR" value={<Money amount={data.mrr} />} />
          <StatCard label="Provider spend" value={<Money amount={data.provider_cost_usd} />}
                    hint="LLM, speech, video, avatars" />
          <StatCard label="Gross margin" value={<Money amount={data.gross_margin_usd} />}
                    hint={data.gross_margin_pct !== null ? `${data.gross_margin_pct}% of MRR` : "no revenue yet"}
                    tone={data.gross_margin_usd >= 0 ? "good" : "bad"} />
        </div>

        {/* Renders made before cost tracking existed have no breakdown. Counting
            them as free would flatter the margin, so they are excluded and
            declared instead. */}
        {(data.renders_unpriced > 0 || data.renders_partly_estimated > 0) && (
          <div className="brutal-card p-4 mt-4 bg-state-warning-surface text-sm" role="status">
            {data.renders_unpriced > 0 && (
              <p>
                <strong>{data.renders_unpriced}</strong> finished render(s) predate cost
                tracking and are excluded from the figures above — not counted as free.
              </p>
            )}
            {data.renders_partly_estimated > 0 && (
              <p className="mt-1">
                <strong>{data.renders_partly_estimated}</strong> of {data.renders_priced} priced
                render(s) include at least one estimated component, because the provider
                returned no usage data. Treat these as indicative, not as an invoice.
              </p>
            )}
          </div>
        )}
      </Section>

      <Section id="costkinds" title="// SPEND BY COMPONENT">
        <DataTable
          caption="Provider spend grouped by what incurred it"
          empty="No priced renders yet — generate a video and it will appear here."
          columns={[{ key: "kind", label: "Component" }, { key: "usd", label: "Spend" },
                    { key: "share", label: "Share" }]}
          rows={kinds}
          renderRow={([kind, usd]) => (
            <tr key={kind} className="border-t border-rule">
              <td className="p-3">{kind}</td>
              <td className="p-3"><Money amount={usd} /></td>
              <td className="p-3 tabular-nums text-ink-muted">
                {data.provider_cost_usd ? Math.round((usd / data.provider_cost_usd) * 100) : 0}%
              </td>
            </tr>
          )}
        />
      </Section>

      <Section
        id="permargin"
        title="// MARGIN BY ACCOUNT"
        actions={worst.length > 0 && (
          <Pill tone="danger">{worst.length} account(s) costing more than they pay</Pill>
        )}
      >
        <DataTable
          caption="Each account's subscription revenue against the provider cost of its renders, worst first"
          empty="No accounts yet."
          columns={[{ key: "email", label: "Account" }, { key: "plan", label: "Plan" },
                    { key: "mrr", label: "MRR" }, { key: "cost", label: "Cost" },
                    { key: "margin", label: "Margin" }]}
          rows={data.users}
          renderRow={(u) => (
            <tr key={u.user_id} className="border-t border-rule">
              <td className="p-3 font-mono-ui text-xs break-all">{u.email}</td>
              <td className="p-3">{u.plan}</td>
              <td className="p-3"><Money amount={u.mrr} /></td>
              <td className="p-3"><Money amount={u.cost_usd} /></td>
              <td className={`p-3 ${u.margin_usd < 0 ? "text-ink-accent" : ""}`}>
                <Money amount={u.margin_usd} />
                {u.margin_pct !== null && (
                  <span className="ml-2 text-2xs text-ink-muted tabular-nums">{u.margin_pct}%</span>
                )}
              </td>
            </tr>
          )}
        />
      </Section>
    </>
  );
}
